/**
 * useDevStatesPage composable
 *
 * Manages tab state and data for the dev UI State Catalogue page.
 * Follows the same pattern as useAboutPage.
 */
import { DIALOG_ERROR_CONFIG, type DialogErrorConfig } from "@/constants/dialogErrorConfig";

export const useDevStatesPage = () => {
  //
  // Composables
  //
  const { t } = useI18n();

  //
  // State
  //
  const activeTab = useCookie<string>("devStatesPage.activeTab", {
    default: () => "errors",
    maxAge: 60 * 60 * 24 * 365, // 1 year
  });

  //
  // Computed
  //

  /** Tab definitions for app bar extension */
  const tabs = computed(() =>
    [
      ["errors", "mdi-alert-circle-outline"],
      ["emptyStates", "mdi-database-off-outline"],
      ["loading", "mdi-loading"],
      ["dialogs", "mdi-card-text-outline"],
      ["misc", "mdi-dots-horizontal-circle-outline"],
    ].map(([value, icon]) => ({
      value,
      icon,
      name: t(`pages.dev.uiStates.tabs.${value}`),
    })),
  );

  /** Fixture text shorthand */
  const f = (key: string) => t(`pages.dev.uiStates.fixture.${key}`);

  /**
   * Classified error types (excluding auth — handled by OIDC auto-redirect).
   * Each entry carries the icon/color from DIALOG_ERROR_CONFIG plus i18n labels.
   */
  const errorTypes = computed(() =>
    (["server", "network", "timeout", "unknown"] as const).map((type) => {
      const cfg = DIALOG_ERROR_CONFIG[type] as DialogErrorConfig;
      return {
        type,
        label: t(`pages.dev.uiStates.labels.${type}`),
        icon: cfg.icon,
        iconColor: cfg.iconColor,
        title: f(`${type}Title`),
        subtitle: f(`${type}Subtitle`),
        actionIcon: cfg.actionIcon,
        usedIn: t(`pages.dev.uiStates.usedIn.${type}`),
      };
    }),
  );

  /** Empty state variations */
  const emptyStates = computed(() => [
    {
      key: "empty",
      icon: "mdi-calendar-blank-outline",
      iconColor: "grey",
      title: f("emptyTitle"),
      subtitle: f("emptySubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.empty"),
    },
    {
      key: "filteredEmpty",
      icon: "mdi-filter-off-outline",
      iconColor: "grey",
      title: f("filteredEmptyTitle"),
      subtitle: f("filteredEmptySubtitle"),
      hasAction: true,
      actionLabel: f("clearFilters"),
      actionIcon: "mdi-filter-off-outline",
      actionColor: "error",
      hasSecondAction: true,
      secondActionLabel: f("editFilters"),
      secondActionIcon: "mdi-filter-cog-outline",
      secondActionColor: "primary",
      usedIn: t("pages.dev.uiStates.usedIn.filteredEmpty"),
    },
    {
      key: "paymentRequired",
      icon: "mdi-cash-clock",
      iconColor: "error",
      title: f("paymentTitle"),
      subtitle: f("paymentSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.paymentRequired"),
    },
    {
      key: "noResults",
      icon: "mdi-clipboard-text-off-outline",
      iconColor: "grey",
      title: f("noResultsTitle"),
      subtitle: f("noResultsSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noResults"),
    },
    {
      key: "noSamples",
      icon: "mdi-test-tube-off",
      iconColor: "grey",
      title: f("noSamplesTitle"),
      subtitle: f("noSamplesSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noSamples"),
    },
    {
      key: "noSales",
      icon: "mdi-cash-off",
      iconColor: "grey",
      title: f("noSalesTitle"),
      subtitle: f("noSalesSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noSales"),
    },
    {
      key: "noProducts",
      icon: "mdi-package-variant-remove",
      iconColor: "grey",
      title: f("noProductsTitle"),
      subtitle: f("noProductsSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noProducts"),
    },
    {
      key: "noPayments",
      icon: "mdi-cash-off",
      iconColor: "grey",
      title: f("noPaymentsTitle"),
      subtitle: f("noPaymentsSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noPayments"),
    },
    {
      key: "noVisits",
      icon: "mdi-calendar-blank-outline",
      iconColor: "grey",
      title: f("noVisitsTitle"),
      subtitle: f("noVisitsSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noVisits"),
    },
    {
      key: "noTests",
      icon: "mdi-test-tube",
      iconColor: "grey",
      title: f("noTestsTitle"),
      subtitle: f("noTestsSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noTests"),
    },
    {
      key: "noFlaggedResults",
      icon: "mdi-flag-off-outline",
      iconColor: "grey",
      title: f("noFlaggedResultsTitle"),
      subtitle: f("noFlaggedResultsSubtitle"),
      usedIn: t("pages.dev.uiStates.usedIn.noFlaggedResults"),
    },
  ]);

  /** Loading skeleton patterns with labels and usedIn info */
  const skeletonPatterns = computed(() => [
    {
      key: "tableRows",
      label: t("pages.dev.uiStates.labels.skeletons.tableRows"),
      usedIn: t("pages.dev.uiStates.usedIn.tableRows"),
    },
    {
      key: "cardAndTable",
      label: t("pages.dev.uiStates.labels.skeletons.cardAndTable"),
      usedIn: t("pages.dev.uiStates.usedIn.cardAndTable"),
    },
    {
      key: "patientHeader",
      label: t("pages.dev.uiStates.labels.skeletons.patientHeader"),
      usedIn: t("pages.dev.uiStates.usedIn.patientHeader"),
    },
    {
      key: "serviceCards",
      label: t("pages.dev.uiStates.labels.skeletons.serviceCards"),
      usedIn: t("pages.dev.uiStates.usedIn.serviceCards"),
    },
  ]);

  /**
   * Dialog state patterns — context-specific empty states per dialog.
   * Loading and error states are now handled via snackbar (dialogs only
   * open when data is available).
   */
  const dialogPatterns = computed(() => [
    {
      key: "historyEmpty",
      patternType: "empty" as const,
      icon: "mdi-history",
      fixtureTitle: "historyEmptyTitle",
      fixtureSubtitle: "historyEmptySubtitle",
      label: t("pages.dev.uiStates.labels.dialogHistoryEmpty"),
      usedIn: t("pages.dev.uiStates.usedIn.dialogHistoryEmpty"),
    },
    {
      key: "resultsEmpty",
      patternType: "empty" as const,
      icon: "mdi-clipboard-text-off-outline",
      fixtureTitle: "noResultsTitle",
      fixtureSubtitle: "noResultsSubtitle",
      label: t("pages.dev.uiStates.labels.dialogResultsEmpty"),
      usedIn: t("pages.dev.uiStates.usedIn.dialogResultsEmpty"),
    },
  ]);

  /** Snackbar variant showcases — one per semantic colour */
  const snackbarVariants = computed(() =>
    (
      [
        ["success", "mdi-check-circle-outline"],
        ["error", "mdi-alert-circle-outline"],
        ["warning", "mdi-alert-outline"],
        ["info", "mdi-information-outline"],
      ] as const
    ).map(([key, icon]) => ({
      key,
      icon,
      color: key as string,
      text: f(`snackbar${key.charAt(0).toUpperCase() + key.slice(1)}Text`),
      label: t(`pages.dev.uiStates.labels.snackbar${key.charAt(0).toUpperCase() + key.slice(1)}`),
      usedIn: t(`pages.dev.uiStates.usedIn.snackbar${key.charAt(0).toUpperCase() + key.slice(1)}`),
    })),
  );

  /** Miscellaneous UI patterns — full-page spinner, coming-soon placeholder */
  const miscPatterns = computed(() => [
    {
      key: "fullPageSpinner",
      label: t("pages.dev.uiStates.labels.fullPageSpinner"),
      usedIn: t("pages.dev.uiStates.usedIn.fullPageSpinner"),
    },
    {
      key: "comingSoon",
      label: t("pages.dev.uiStates.labels.comingSoon"),
      usedIn: t("pages.dev.uiStates.usedIn.comingSoon"),
    },
  ]);

  /** Fixture text helper — resolves i18n key under the fixture namespace */
  const fixture = (key: string) => f(key);

  return {
    activeTab,
    tabs: readonly(tabs),
    errorTypes: readonly(errorTypes),
    emptyStates: readonly(emptyStates),
    skeletonPatterns: readonly(skeletonPatterns),
    dialogPatterns: readonly(dialogPatterns),
    snackbarVariants: readonly(snackbarVariants),
    miscPatterns: readonly(miscPatterns),
    fixture,
  } as const;
};
